/**
 * OceanContainerChargeBreakdown.jsx
 * Module: BookingHistory / Booking Detail
 *
 * The Charge Breakdown card body on the ocean booking-detail page
 * (OceanCargoDetails.jsx): container filter chips (All / 1 × 20GP / …) over
 * collapsible per-container cards, each split into Origin → Freight →
 * Destination with a "qty @ rate" line and a basis tag per charge. Same
 * layout as the rate row's Charge Breakdown popover, rendered inline.
 *
 * Every amount goes through `formatAmount`, so the page can show its own
 * "$ 1,234.00 (₹ 1,02,000.00)" pairs.
 */
import React, { useId, useState } from "react";
import { Box, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  SECTION_ORDER,
  containerLabel,
  formatMoney,
  formatWhole,
} from "../../forms/SearchRates/ShipmentRate/ContainerRates/groupChargesByContainer";

const C = {
  brand: "#EE9B1E",
  price: "#E8890C",
  divider: "#F1E6CF",
  text: "#111827",
  secondary: "#6B7280",
  muted: "#9CA3AF",
  error: "#C0392B",
  rowBg: "#FFFBF1",
  blText: "#8A4E05",
  blBg: "#FFF4E2",
  equipText: "#6B7280",
  equipBg: "#F1F2F4",
  naBorder: "#E2CFA6",
  naBg: "#FFFDF8",
  border: "#E5E7EB",
};

const ALL = "__all__";

const focusRing = {
  "&:focus-visible": { outline: `2px solid ${C.price}`, outlineOffset: "2px" },
};

const buttonReset = {
  font: "inherit",
  margin: 0,
  border: 0,
  background: "none",
  cursor: "pointer",
  textAlign: "left",
};

// Same leg styling as the offer card's Charge Breakdown in OceanMainRateCom.
const SECTION_CFG = {
  Origin: { title: "Origin Charges", tag: "#1F9254", bg: "#E7F6EC" },
  Freight: { title: "Freight Charges", tag: "#2E6FF2", bg: "#EAF1FF" },
  Destination: { title: "Destination Charges", tag: "#1F9254", bg: "#E7F6EC" },
};

const BasisTag = ({ basis }) => {
  const isBl = basis === "B/L";
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9.5,
        fontWeight: 600,
        lineHeight: 1.5,
        px: "6px",
        borderRadius: "4px",
        color: isBl ? C.blText : C.equipText,
        bgcolor: isBl ? C.blBg : C.equipBg,
        whiteSpace: "nowrap",
      }}
    >
      {basis}
    </Box>
  );
};

const ChargeRow = ({ line, first, formatAmount }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 1.5,
      px: 1.5,
      py: 0.875,
      borderTop: first ? "none" : `1px solid ${C.divider}`,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.text, lineHeight: 1.35 }}>
        {line.name}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 0.375 }}>
        <Typography component="span" sx={{ fontSize: 10.5, color: C.muted, whiteSpace: "nowrap" }}>
          {line.qty} @ {formatMoney(line.rate)} {line.rateCurrency}
        </Typography>
        <BasisTag basis={line.basis} />
      </Box>
    </Box>
    <Typography component="div" sx={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: "nowrap", textAlign: "right" }}>
      {formatAmount(line.amount)}
    </Typography>
  </Box>
);

// collapsible=false → always open, plain header with no toggle/arrow.
const ContainerGroupCard = ({ group, open: openProp, onToggle, idPrefix, formatAmount, collapsible = true }) => {
  const panelId = `${idPrefix}-${group.key}`;
  const open = collapsible ? openProp : true;
  const headerProps = collapsible
    ? { component: "button", type: "button", "aria-expanded": open, "aria-controls": panelId, onClick: onToggle }
    : {};
  return (
    <Box
      sx={{
        flexShrink: 0,
        border: `1px solid ${C.divider}`,
        borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        {...headerProps}
        sx={{
          ...(collapsible ? { ...buttonReset, ...focusRing } : {}),
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: C.rowBg,
          borderRadius: open ? "10px 10px 0 0" : "10px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap" }}>
            {containerLabel(group)}
          </Typography>
          {group.weightKg > 0 && (
            <Typography component="span" noWrap sx={{ fontSize: 10.5, color: C.muted }}>
              {formatWhole(group.weightKg)} kg
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.price, whiteSpace: "nowrap" }}>
            {formatAmount(group.total)}
          </Typography>
          {collapsible && (
            <KeyboardArrowDownIcon
              aria-hidden
              sx={{
                fontSize: 18,
                color: C.secondary,
                transition: "transform 150ms",
                transform: open ? "rotate(180deg)" : "none",
              }}
            />
          )}
        </Box>
      </Box>

      {open && (
        <Box
          id={panelId}
          sx={{
            borderTop: `1px solid ${C.divider}`,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 1,
          }}
        >
          {group.sections.map((s) => {
            const cfg = SECTION_CFG[s.section] || SECTION_CFG.Freight;
            return (
              <Box
                key={s.section}
                sx={{ border: `1.5px solid ${cfg.tag}`, borderRadius: "8px", overflow: "hidden" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: cfg.bg,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    <Box sx={{ bgcolor: "#EFA603", borderRadius: "4px", px: "6px", py: "1px", flexShrink: 0 }}>
                      <Typography sx={{ fontSize: 9.5, color: "#fff", fontWeight: 700 }}>Required</Typography>
                    </Box>
                    <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color: cfg.tag }}>
                      {cfg.title}
                    </Typography>
                  </Box>
                  <Typography
                    component="div"
                    sx={{ fontSize: 12.5, fontWeight: 700, color: cfg.tag, whiteSpace: "nowrap", flexShrink: 0, textAlign: "right" }}
                  >
                    {formatAmount(s.subtotal)}
                  </Typography>
                </Box>
                {s.lines.map((l, i) => (
                  <ChargeRow key={`${s.section}-${i}`} line={l} first={i === 0} formatAmount={formatAmount} />
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// Orange outlined container chip, e.g. "4 × 20GP".
const ContainerChip = ({ group }) => (
  <Box
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1.4,
      px: 1,
      py: "2px",
      borderRadius: "999px",
      border: `1px solid ${C.brand}`,
      bgcolor: C.blBg,
      color: C.price,
      whiteSpace: "nowrap",
    }}
  >
    {containerLabel(group)}
  </Box>
);

// "All" view, inside a section card: one collapsible row per container;
// opening it lists that container's charge lines for the section.
const ContainerAccordionRow = ({ item, open, onToggle, formatAmount, panelId }) => {
  const { group, lines, subtotal } = item;
  let hasBl = false;
  let hasEquipment = false;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].basis === "B/L") hasBl = true;
    else hasEquipment = true;
  }
  return (
    <Box sx={{ borderTop: `1px solid ${C.divider}` }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        sx={{
          ...buttonReset,
          ...focusRing,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0.875,
          bgcolor: open ? C.rowBg : "#fff",
          "&:hover": { bgcolor: C.rowBg },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flexWrap: "wrap" }}>
          <KeyboardArrowDownIcon
            aria-hidden
            sx={{
              fontSize: 18,
              color: C.secondary,
              transition: "transform 150ms",
              transform: open ? "none" : "rotate(-90deg)",
            }}
          />
          <ContainerChip group={group} />
          {hasEquipment && <BasisTag basis="Equipment" />}
          {hasBl && <BasisTag basis="B/L" />}
        </Box>
        <Typography
          component="div"
          sx={{ fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: "nowrap", flexShrink: 0, textAlign: "right" }}
        >
          {formatAmount(subtotal)}
        </Typography>
      </Box>
      {open && (
        <Box id={panelId} sx={{ borderTop: `1px dashed ${C.divider}`, pl: 2 }}>
          {lines.map((l, i) => (
            <ChargeRow key={i} line={l} first={i === 0} formatAmount={formatAmount} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Pivots container groups into Origin → Freight → Destination, each holding
// one entry per container that has lines in that section.
const groupBySection = (priced) => {
  const out = [];
  for (let s = 0; s < SECTION_ORDER.length; s++) {
    const section = SECTION_ORDER[s];
    const containers = [];
    let subtotal = 0;
    let lineCount = 0;
    for (let g = 0; g < priced.length; g++) {
      const match = priced[g].sections.find((x) => x.section === section);
      if (!match) continue;
      containers.push({ group: priced[g], lines: match.lines, subtotal: match.subtotal });
      subtotal += match.subtotal;
      lineCount += match.lines.length;
    }
    if (containers.length > 0) out.push({ section, containers, subtotal, lineCount });
  }
  return out;
};

// "All" view section card: header, container rows, Total row.
const SectionCard = ({ section: s, expanded, toggle, formatAmount, idPrefix }) => {
  const cfg = SECTION_CFG[s.section] || SECTION_CFG.Freight;
  return (
    <Box sx={{ border: `1.5px solid ${cfg.tag}`, borderRadius: "8px", overflow: "hidden", bgcolor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: cfg.bg,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Box sx={{ bgcolor: "#EFA603", borderRadius: "4px", px: "6px", py: "1px", flexShrink: 0 }}>
            <Typography sx={{ fontSize: 9.5, color: "#fff", fontWeight: 700 }}>Required</Typography>
          </Box>
          <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color: cfg.tag }}>
            {cfg.title}
          </Typography>
        </Box>
        <Typography
          component="div"
          sx={{ fontSize: 12.5, fontWeight: 700, color: cfg.tag, whiteSpace: "nowrap", flexShrink: 0, textAlign: "right" }}
        >
          {formatAmount(s.subtotal)}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 10.5, color: C.muted, px: 1.5, py: 0.5 }}>
        {s.lineCount} line items
      </Typography>
      {s.containers.map((item) => {
        const rowKey = `${s.section}:${item.group.key}`;
        return (
          <ContainerAccordionRow
            key={item.group.key}
            item={item}
            open={!!expanded[rowKey]}
            onToggle={() => toggle(rowKey)}
            formatAmount={formatAmount}
            panelId={`${idPrefix}-${s.section}-${item.group.key}`}
          />
        );
      })}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.875,
          borderTop: `1px solid ${C.divider}`,
          bgcolor: cfg.bg,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.text }}>Total</Typography>
        <Typography
          component="div"
          sx={{ fontSize: 12, fontWeight: 700, color: cfg.tag, whiteSpace: "nowrap", textAlign: "right" }}
        >
          {formatAmount(s.subtotal)}
        </Typography>
      </Box>
    </Box>
  );
};

const chipSx = (active) => ({
  ...buttonReset,
  ...focusRing,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.4,
  px: 1.25,
  py: 0.375,
  borderRadius: "999px",
  border: `1px solid ${active ? C.brand : C.border}`,
  bgcolor: active ? C.blBg : "#fff",
  color: active ? C.blText : C.secondary,
  whiteSpace: "nowrap",
  "&:hover": { borderColor: C.brand },
});

/**
 * @param {Object} props
 * @param {import("../../forms/SearchRates/ShipmentRate/ContainerRates/groupChargesByContainer").ContainerBreakdown} props.breakdown
 * @param {(amount: number) => React.ReactNode} props.formatAmount - renders
 *   every group / section / line amount
 */
const OceanContainerChargeBreakdown = ({ breakdown, formatAmount }) => {
  const { priced, unavailable } = breakdown;
  const idPrefix = useId();
  const [active, setActive] = useState(ALL);
  const sections = groupBySection(priced);
  // "All" view keys: `${section}:${containerKey}`. Container rows start collapsed.
  const [expanded, setExpanded] = useState({});

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const selectChip = (key) => setActive(key);

  const selected = active === ALL ? null : priced.find((g) => g.key === active);

  return (
    <Box sx={{ mb: "5px" }}>
      {/* Filter chips */}
      <Box
        role="toolbar"
        aria-label="Filter by container"
        sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pb: 1.25 }}
      >
        <Box
          component="button"
          type="button"
          aria-pressed={active === ALL}
          onClick={() => selectChip(ALL)}
          sx={chipSx(active === ALL)}
        >
          All
        </Box>
        {priced.map((g) => (
          <Box
            key={g.key}
            component="button"
            type="button"
            aria-pressed={active === g.key}
            onClick={() => selectChip(g.key)}
            sx={chipSx(active === g.key)}
          >
            {containerLabel(g)}
          </Box>
        ))}
        {unavailable.map((c) => (
          <Box
            key={c.key}
            component="span"
            title="Rate not available"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1.4,
              px: 1.25,
              py: 0.375,
              borderRadius: "999px",
              border: `1px dashed ${C.naBorder}`,
              color: C.muted,
              textDecoration: "line-through",
              cursor: "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            {containerLabel(c)}
          </Box>
        ))}
      </Box>

      {/* Container groups */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {active === ALL
          ? sections.map((s) => (
              <SectionCard
                key={s.section}
                section={s}
                expanded={expanded}
                toggle={toggle}
                formatAmount={formatAmount}
                idPrefix={idPrefix}
              />
            ))
          : selected && (
              <ContainerGroupCard
                group={selected}
                idPrefix={idPrefix}
                formatAmount={formatAmount}
                collapsible={false}
              />
            )}

        {active === ALL &&
          unavailable.map((c) => (
            <Box
              key={c.key}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 1,
                px: 1.5,
                py: 1,
                border: `1px dashed ${C.naBorder}`,
                borderRadius: "10px",
                bgcolor: C.naBg,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: C.muted, whiteSpace: "nowrap" }}>
                  {containerLabel(c)}
                </Typography>
                {c.weightKg > 0 && (
                  <Typography component="span" noWrap sx={{ fontSize: 10.5, color: C.muted }}>
                    {formatWhole(c.weightKg)} kg
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: C.error, whiteSpace: "nowrap" }}>
                Rate not available
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default OceanContainerChargeBreakdown;
